import { useEffect, useState, useCallback } from 'react'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { boxApi } from '../utils/api'

function money(value) {
  const n = Number(value) || 0
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function scoreColor(score) {
  if (score >= 75) return 'text-green-600'
  if (score >= 50) return 'text-amber-600'
  return 'text-red-600'
}

const SEVERITY_STYLES = {
  critical: 'bg-red-50 text-red-700 border-red-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  )
}

export default function BoxAdvisor({ boxId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await boxApi.getInsights(boxId)
      setData(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [boxId])

  useEffect(() => {
    load()
  }, [load])

  const runAnalysis = async () => {
    try {
      setAnalyzing(true)
      setError(null)
      const result = await boxApi.analyze(boxId)
      setData(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setAnalyzing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center text-gray-500">
        Loading insights...
      </div>
    )
  }

  const metrics = data?.metrics
  const score = data?.health_score ?? 0
  const ai = data?.ai
  const totals = metrics?.contribution_health?.totals
  const members = metrics?.contribution_health?.members ?? []
  const behind = members.filter((m) => !m.on_track)
  const flags = metrics?.risk_flags ?? []

  return (
    <div className="space-y-6 p-6">
      {/* Header + Analyze */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center text-lg font-semibold text-gray-900">
            <SparklesIcon className="mr-2 h-5 w-5 text-primary-500" />
            Box Advisor
          </h3>
          <p className="text-sm text-gray-500">
            A health check of contributions, payouts, and schedule for this box.
          </p>
        </div>
        <button
          onClick={runAnalysis}
          disabled={analyzing}
          className="shrink-0 rounded-xl bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-60"
        >
          {analyzing ? 'Analyzing…' : 'Analyze'}
        </button>
      </div>

      {error && (
        <div className="flex items-start justify-between rounded-md bg-red-50 p-4">
          <p className="text-red-800">Error: {error}</p>
          <button onClick={load} className="text-sm font-medium text-red-700 underline">
            Retry
          </button>
        </div>
      )}

      {metrics && (
        <>
          {/* Health header */}
          <div className="rounded-2xl border border-gray-100 bg-white p-6">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <div className="text-center sm:text-left">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Box health
                </p>
                <p className={`text-4xl font-bold ${scoreColor(score)}`}>{score}<span className="text-xl text-gray-400">/100</span></p>
              </div>
              <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat
                  label="Paid in"
                  value={money(totals?.paid_in_to_date)}
                  sub={`of ${money(totals?.expected_to_date)} expected`}
                />
                <Stat label="Members behind" value={`${behind.length} / ${members.length}`} />
                <Stat
                  label="Cycle complete"
                  value={`${metrics.timeline.percent_complete}%`}
                  sub={`month ${metrics.timeline.months_elapsed} of ${metrics.box_summary.cycle_duration_months}`}
                />
                <Stat
                  label="Unassigned slots"
                  value={metrics.payout_distribution.unassigned_slots}
                  sub={`of ${metrics.box_summary.total_slots} total`}
                />
              </div>
            </div>
          </div>

          {/* Advice section */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-900">Advice</h4>
            {ai ? (
              <div className="space-y-3">
                {ai.headline && <p className="text-sm text-gray-700">{ai.headline}</p>}
                {ai.insights.map((insight) => (
                  <div
                    key={insight.id}
                    className={`rounded-xl border p-4 ${SEVERITY_STYLES[insight.severity] || SEVERITY_STYLES.info}`}
                  >
                    <p className="text-sm font-semibold">{insight.title}</p>
                    <p className="mt-1 text-sm">{insight.recommendation}</p>
                  </div>
                ))}
                {ai.suggested_actions?.length > 0 && (
                  <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Suggested next steps
                    </p>
                    <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700">
                      {ai.suggested_actions.map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ol>
                  </div>
                )}
                <p className="pt-1 text-xs text-gray-400">
                  AI suggestions — the numbers above are computed by the app, not the AI.
                </p>
              </div>
            ) : flags.length ? (
              <div className="space-y-2">
                {flags.map((flag) => (
                  <div
                    key={flag.id}
                    className={`rounded-xl border px-4 py-3 text-sm ${SEVERITY_STYLES[flag.severity] || SEVERITY_STYLES.info}`}
                  >
                    {flag.detail}
                  </div>
                ))}
                <p className="pt-1 text-xs text-gray-400">
                  {data?.ai_error
                    ? `AI advice unavailable: ${data.ai_error}`
                    : 'Press Analyze to get AI-written advice on top of these automatic checks.'}
                </p>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 text-sm text-gray-600">
                Everything looks healthy — no issues detected.
              </div>
            )}
          </div>

          {/* Per-member breakdown */}
          {members.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-6 py-4">
                <h4 className="text-sm font-semibold text-gray-900">Member breakdown</h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Member', 'Paid in', 'Expected', 'Balance', 'Payouts', 'Status'].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {members.map((m) => (
                      <tr key={m.member_id}>
                        <td className="px-4 py-3 font-medium text-gray-900">{m.name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{money(m.paid_in_to_date)}</td>
                        <td className="px-4 py-3 text-gray-600">{money(m.expected_to_date)}</td>
                        <td className={`px-4 py-3 ${m.balance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {money(m.balance)}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{m.slots}</td>
                        <td className="px-4 py-3">
                          {m.on_track ? (
                            <span className="text-green-600">On track</span>
                          ) : (
                            <span className="text-red-600">{m.missed_periods} behind</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data?.generated_at && (
            <p className="text-xs text-gray-400">
              Last analyzed {new Date(data.generated_at).toLocaleString()}
            </p>
          )}
        </>
      )}
    </div>
  )
}
